import axios from "axios";
import apiClient, { apiRequest } from "./client";
import { api } from "./typed";

/**
 * Consultant profile photos (#776, BE #123/#434/#705).
 *
 * Photo serving is deliberately behind auth (BE #434) and the access token
 * lives in memory only, so photos can never be plain `<img src>` URLs — the
 * bytes are fetched through the authenticated client as a Blob.
 *
 * Uploads are multipart/form-data, which the typed client cannot model (it
 * only extracts `application/json` bodies — a typed call would compile with
 * `body: never` and silently send nothing), so they go through the raw axios
 * instance. The explicit multipart content type is REQUIRED: the instance
 * defaults to `application/json`, and axios then runs `formDataToJSON` on a
 * FormData body — the file part is destroyed and the backend rejects with
 * "file: Field required". Declaring multipart makes axios keep the FormData
 * and set the boundary. The deletes are plain JSON endpoints and stay on the
 * typed client.
 */
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };
export const photosAPI = {
  /**
   * GET /consultants/{id}/photo — the stored photo as a Blob, or null when
   * the consultant has none (404 is an expected state, not an error; the
   * interceptor toast is suppressed for the same reason).
   */
  getConsultantPhoto: async (consultantId: number): Promise<Blob | null> => {
    try {
      return await apiRequest.get<Blob>(`/consultants/${consultantId}/photo`, {
        responseType: "blob",
        suppressErrorToast: true,
      });
    } catch (error) {
      // With responseType "blob" the error body is a Blob — branch on the
      // status only, never the body.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /** POST /consultants/me/photo — upload/replace the signed-in consultant's photo. */
  uploadMyPhoto: async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append("file", file);
    await apiClient.post("/consultants/me/photo", formData, MULTIPART);
  },

  /** DELETE /consultants/me/photo */
  deleteMyPhoto: async (): Promise<void> => {
    await api.delete("/consultants/me/photo");
  },

  /** PUT /admin/consultants/{id}/photo — admin upload/replace for a consultant. */
  adminUploadPhoto: async (consultantId: number, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append("file", file);
    await apiClient.put(`/admin/consultants/${consultantId}/photo`, formData, MULTIPART);
  },

  /** DELETE /admin/consultants/{id}/photo */
  adminDeletePhoto: async (consultantId: number): Promise<void> => {
    await api.delete("/admin/consultants/{consultant_id}/photo", {
      path: { consultant_id: consultantId },
    });
  },
};
